/**
 * Cart utility functions for managing cart in localStorage
 */

import { getAvailableQuantity, getStockStatusFromProduct } from './productStockUtils';

/**
 * Get cart items from localStorage
 * @returns {Array} Array of cart items
 */
export const getCartItems = () => {
  try {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      return JSON.parse(savedCart);
    }
  } catch (error) {
    console.error('Error loading cart from localStorage:', error);
  }
  return [];
};

/**
 * Save cart items to localStorage
 * @param {Array} items - Array of cart items
 */
export const saveCartItems = (items) => {
  try {
    localStorage.setItem('cart', JSON.stringify(items));
    // Dispatch custom event to notify other components
    window.dispatchEvent(new Event('cartUpdated'));
    // Also trigger storage event for cross-tab updates
    window.dispatchEvent(new StorageEvent('storage', { key: 'cart' }));
  } catch (error) {
    console.error('Error saving cart to localStorage:', error);
  }
};

/**
 * Add a product to cart
 * @param {Object} product - Product object to add
 * @param {Number} quantity - Quantity to add (default: 1)
 * @returns {Array} Updated cart items
 */
export const addToCart = (product, quantity = 1) => {
  const cartItems = getCartItems();

  if (getStockStatusFromProduct(product) === 'out-of-stock') {
    return cartItems;
  }

  const maxQty = getAvailableQuantity(product);
  const cappedAdd = maxQty > 0 ? Math.min(quantity, maxQty) : 0;
  if (cappedAdd < 1) return cartItems;

  const stockStr = getStockStatusFromProduct(product);
  const stockQuantity = Number.isFinite(maxQty) ? maxQty : undefined;

  // Check if product already exists in cart
  const existingItemIndex = cartItems.findIndex(
    item => item.product && item.product._id === product._id
  );

  if (existingItemIndex >= 0) {
    const existingItem = cartItems[existingItemIndex];
    const merged = existingItem.quantity + cappedAdd;
    const newQuantity = maxQty > 0 ? Math.min(merged, maxQty) : existingItem.quantity;
    cartItems[existingItemIndex] = {
      ...existingItem,
      product: {
        ...existingItem.product,
        stock: stockStr,
        ...(stockQuantity !== undefined ? { stockQuantity } : {}),
      },
      quantity: newQuantity,
      subtotal: existingItem.product.price * newQuantity
    };
  } else {
    const newItem = {
      _id: product._id || `cart-${Date.now()}-${Math.random()}`,
      product: {
        _id: product._id,
        name: product.name,
        price: product.price,
        unit: product.unit,
        image: product.image || product.images?.[0] || null,
        farmer: product.farmer || {
          _id: product.farmer?._id,
          name: product.farmer?.name || 'Unknown Farmer',
          district: product.farmer?.district || '',
          location: product.farmer?.location || ''
        },
        isOrganic: product.isOrganic || false,
        stock: stockStr,
        ...(stockQuantity !== undefined ? { stockQuantity } : {}),
      },
      quantity: cappedAdd,
      subtotal: product.price * cappedAdd
    };
    cartItems.push(newItem);
  }

  saveCartItems(cartItems);
  return cartItems;
};

/**
 * Update quantity of a cart item
 * @param {String} itemId - Cart item ID
 * @param {Number} newQuantity - New quantity
 * @returns {Array} Updated cart items
 */
export const updateCartQuantity = (itemId, newQuantity) => {
  const cartItems = getCartItems();
  
  if (newQuantity < 1) {
    return removeFromCart(itemId);
  }

  const updatedItems = cartItems.map(item => {
    if (item._id === itemId) {
      const max =
        item.product?.stockQuantity != null && Number.isFinite(Number(item.product.stockQuantity))
          ? Math.max(0, Number(item.product.stockQuantity))
          : null;
      const qty = max != null ? Math.min(newQuantity, max) : newQuantity;
      return {
        ...item,
        quantity: qty,
        subtotal: item.product.price * qty
      };
    }
    return item;
  });

  saveCartItems(updatedItems);
  return updatedItems;
};

/**
 * Remove an item from cart
 * @param {String} itemId - Cart item ID
 * @returns {Array} Updated cart items
 */
export const removeFromCart = (itemId) => {
  const cartItems = getCartItems();
  const updatedItems = cartItems.filter(item => item._id !== itemId);
  saveCartItems(updatedItems);
  return updatedItems;
};

/**
 * Clear all items from cart
 */
export const clearCart = () => {
  localStorage.removeItem('cart');
  // Dispatch custom event to notify other components
  window.dispatchEvent(new Event('cartUpdated'));
  // Also trigger storage event for cross-tab updates
  window.dispatchEvent(new StorageEvent('storage', { key: 'cart' }));
};

/**
 * Get total number of items in cart
 * @returns {Number} Total quantity of all items
 */
export const getCartItemCount = () => {
  const cartItems = getCartItems();
  return cartItems.reduce((sum, item) => sum + item.quantity, 0);
};

/**
 * Get total price of cart
 * @returns {Number} Total price
 */
export const getCartTotal = () => {
  const cartItems = getCartItems();
  return cartItems.reduce((sum, item) => sum + item.subtotal, 0);
};

